// FILE: components/pages/PathListPage.tsx
"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { Search, Route, Loader2 } from "lucide-react";
import PathCard from "@/components/cards/PathCard";
import BackToTop from "@/components/shared/BackToTop";
import { GRADIENTS } from "@/components/explore/_shared/mappers";
import { buildLandingUrl } from "@/lib/seo/slug";
import { useEntitySearch } from "@/hooks/useEntitySearch";

/* ═══ UI item type ═══ */
interface PathUI {
  id: string;
  slug: string;
  title: string;
  author: string;
  authorId: string;
  authorUsername: string | null;
  emoji: string;
  org?: string;
  orgHref?: string;
  position?: string;
  positionHref?: string;
  rating: number;
  students: number;
  questions: number;
  demoQuestions: number;
  price: number | "free";
  gradient: string;
  isUnlocked: boolean;
  progress: number;
}

function mapPath(row: any, idx: number): PathUI {
  const author = row.creator?.full_name ?? row.creator?.username ?? "";
  const authorId = row.user_id ?? row.creator?.id ?? "";
  const authorUsername = row.creator?.username ?? null;
  const orgName = row.org?.name ?? "";
  const orgSlug = row.org?.slug;
  const positionName = row.position?.name ?? "";
  const positionSlug = row.position?.slug;
  const domainSlug = row.domain?.slug;

  const orgHref =
    domainSlug && orgSlug
      ? buildLandingUrl("paths", { domain: domainSlug, org: orgSlug })
      : undefined;
  const positionHref =
    domainSlug && orgSlug && positionSlug
      ? buildLandingUrl("paths", { domain: domainSlug, org: orgSlug, position: positionSlug })
      : undefined;

  return {
    id: row.id,
    slug: row.slug ?? "",
    title: row.title ?? "Lộ trình",
    author: author || "Lộ trình học",
    authorId,
    authorUsername,
    emoji: row.emoji ?? "📚",
    org: orgName || undefined,
    orgHref,
    position: positionName || undefined,
    positionHref,
    rating: row.average_rating ?? 0,
    students: row.total_enrolled ?? 0,
    questions: row.total_questions ?? 0,
    demoQuestions: row.demo_limit ?? Math.min(10, row.total_questions ?? 0),
    price: !row.price || row.price === 0 ? "free" : row.price,
    gradient: GRADIENTS[idx % GRADIENTS.length],
    isUnlocked: row.is_unlocked ?? false,
    progress: row.progress ?? 0,
  };
}

/* ═══ Skeleton ═══ */
function PathSkeleton() {
  return (
    <div className="bg-white border border-[#e2e8f0] rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,.04)] overflow-hidden animate-pulse">
      <div className="flex gap-3 p-4">
        <div className="w-20 h-20 rounded-xl bg-[#e2e8f0] shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-3/4 bg-[#e2e8f0] rounded" />
          <div className="h-3 w-1/2 bg-[#e2e8f0] rounded" />
          <div className="h-3 w-1/3 bg-[#e2e8f0] rounded" />
        </div>
      </div>
    </div>
  );
}

const SORT_OPTIONS = [
  { value: "newest", label: "Mới nhất" },
  { value: "popular", label: "Phổ biến" },
  { value: "rating", label: "Đánh giá cao" },
];

const STATUS_PILLS = [
  { value: "all", label: "Tất cả" },
  { value: "free", label: "Miễn phí" },
  { value: "paid", label: "Trả phí" },
];

export default function PathListPage({
  initialData = [],
  currentSort = "newest",
  currentSearch = "",
}: {
  initialData?: any[];
  currentSort?: string;
  currentSearch?: string;
  onBack?: () => void;
}) {
  const [statusFilter, setStatusFilter] = useState("all");

  // ⚡ Tìm kiếm + sort chạy qua /api/paths (JSON vài KB).
  // Trước đây router.push → Next render lại TOÀN BỘ trang mỗi lần gõ.
  const { data, loading, query, setQuery, params, setParam } = useEntitySearch<any>({
    endpoint: "/api/paths",
    initialData,
    initialQuery: currentSearch,
    initialParams: { sort: currentSort, limit: 30 },
    minChars: 2,
    debounceMs: 280,
  });

  const sort = String(params.sort ?? "newest");

  // ⚡ replaceState: URL vẫn share/bookmark được nhưng KHÔNG trigger navigation.
  // router.push sẽ render lại server component → đúng cái ta vừa bỏ.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const sp = new URLSearchParams(window.location.search);
    const q = query.trim();
    if (q) sp.set("q", q);
    else sp.delete("q");
    if (sort && sort !== "newest") sp.set("sort", sort);
    else sp.delete("sort");
    const qs = sp.toString();
    const next = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    if (next !== window.location.pathname + window.location.search) {
      window.history.replaceState(null, "", next);
    }
  }, [query, sort]);

  const paths = useMemo<PathUI[]>(() => data.map((row, i) => mapPath(row, i)), [data]);

  const filtered = useMemo(
    () =>
      paths.filter(
        (p) =>
          statusFilter === "all" ||
          (statusFilter === "free" ? p.price === "free" : p.price !== "free"),
      ),
    [paths, statusFilter],
  );

  // ⚡ IntersectionObserver trong useEffect + disconnect.
  // Bản cũ đặt trong ref callback → tạo observer MỚI mỗi render, không bao giờ dọn.
  const stickyRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = stickyRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([e]) => el.classList.toggle("stuck", !e.isIntersecting),
      { threshold: [1], rootMargin: "-1px 0px 0px 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="max-w-[60rem] mx-auto px-6 py-6 max-md:px-3.5">
      {/* Header */}
      <div className="mb-5">
        <div className="flex items-center gap-2.5 mb-1">
          <Route className="w-5 h-5 text-teal-600" />
          <h1 className="font-display text-xl font-extrabold text-teal-800">
            Lộ trình học chi tiết
          </h1>
        </div>
        <p className="text-[13px] text-[#64748b]">
          Lộ trình ôn thi bài bản từ các chuyên gia hàng đầu
        </p>
      </div>

      {/* Sticky filter */}
      <div
        ref={stickyRef}
        className="sticky top-0 z-20 -mx-6 px-6 py-3 bg-[#f1f5f9]/95 backdrop-blur-sm border-b border-transparent [&.stuck]:border-[#e2e8f0] [&.stuck]:shadow-[0_2px_8px_rgba(0,0,0,.06)] transition-all mb-5"
      >
        <div className="flex items-center gap-2.5 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#64748b]" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Tìm tên lộ trình..."
              className="w-full h-9 rounded-full border border-[#e2e8f0] bg-white pl-9 pr-4 text-[12px] font-medium outline-none focus:border-teal-600 focus:shadow-[0_0_0_3px_rgba(30,90,112,.06)] transition-all placeholder:text-[#94a3b8]"
            />
          </div>

          {STATUS_PILLS.map((f) => (
            <button
              key={f.value}
              onClick={() => setStatusFilter(f.value)}
              className={`px-3.5 py-[5px] rounded-full text-[11px] font-semibold border whitespace-nowrap transition-all ${
                statusFilter === f.value
                  ? "bg-teal-800 text-white border-teal-800"
                  : "border-[#e2e8f0] bg-white text-[#64748b] hover:border-teal-500 hover:text-teal-700"
              }`}
            >
              {f.label}
            </button>
          ))}

          <select
            value={sort}
            onChange={(e) => setParam("sort", e.target.value)}
            className="h-9 rounded-full border border-[#e2e8f0] bg-white px-3.5 text-[12px] font-semibold text-[#0f172a] outline-none cursor-pointer appearance-none pr-8 bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%2364748b%22%20stroke-width%3D%222%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22/%3E%3C/svg%3E')] bg-no-repeat bg-[right_0.75rem_center] focus:border-teal-600 transition-all ml-auto"
          >
            {SORT_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>

          <span className="text-[11px] text-[#64748b] font-medium">
            {loading ? (
              <Loader2 className="w-3 h-3 animate-spin inline" />
            ) : (
              `${filtered.length} lộ trình`
            )}
          </span>
        </div>
      </div>

      {/* Grid */}
      {loading && paths.length === 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          {Array.from({ length: 6 }).map((_, i) => (
            <PathSkeleton key={i} />
          ))}
        </div>
      ) : (
        // ⚡ Giữ kết quả cũ mờ đi thay vì xóa trắng → không nhảy layout khi gõ
        <div
          className={`grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8 transition-opacity ${
            loading ? "opacity-50" : "opacity-100"
          }`}
        >
          {filtered.map((p) => (
            <PathCard key={p.id} {...p} variant="list" />
          ))}
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="text-center py-16">
          <div className="text-3xl mb-3">📚</div>
          <p className="text-sm text-[#64748b]">Không tìm thấy lộ trình nào phù hợp</p>
        </div>
      )}

      <BackToTop />
    </div>
  );
}